import React from 'react';
import './LoadingAnimation.css';

/**
 * LoadingAnimation component with multiple creative variants
 * Incorporates FLARETECH flame icon with various animation styles
 * 
 * @param {Object} props
 * @param {('spark-orbit'|'flame-pulse'|'dual-ring'|'perspective-spin')} props.variant - Animation style
 * @param {('small'|'medium'|'large')} props.size - Size variant
 * @param {string} props.className - Additional CSS classes
 */
const LoadingAnimation = ({ 
  variant = 'spark-orbit', 
  size = 'medium',
  className = '' 
}) => {
  
  const renderAnimation = () => {
    switch(variant) {
      case 'spark-orbit':
        return (
          <div className="loading-spark-orbit">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i} 
                className="spark" 
                style={{
                  '--angle': `${i * 45}deg`,
                  '--delay': `${i * 0.12}s`
                }}
              />
            ))}
            <div className="flame-icon">
              <svg viewBox="0 0 230 270" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="grad1-spark" x1="227.87" y1="108.66" x2="145.91" y2="260.44" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ffb810"/>
                    <stop offset="0.43" stopColor="#fa406d"/>
                    <stop offset="1" stopColor="#ed2127"/>
                  </linearGradient>
                  <linearGradient id="grad2-spark" x1="120.81" y1="25.04" x2="188.76" y2="234.11" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ed2127"/>
                    <stop offset="0.57" stopColor="#fa406d"/>
                    <stop offset="1" stopColor="#ffb810"/>
                  </linearGradient>
                </defs>
                <g transform="translate(-87, -20)">
                  <path fill="url(#grad1-spark)" d="M226.4,101.17a85.3,85.3,0,0,1,2.3,48.86,61.26,61.26,0,0,1,2.52,17A64.12,64.12,0,0,1,103,167v0a85.94,85.94,0,0,0-.6,10,85.19,85.19,0,1,0,124-75.81Z"/>
                  <path fill="url(#grad2-spark)" d="M229,151c-.09-.32-.17-.64-.26-1-5.83-20.31-26.67-50.54-38.05-69.19-4.92-8.06-9.18-16.1-11.8-24.22a53,53,0,0,1-2.75-15.81,44,44,0,0,1,4.8-20.34,67.78,67.78,0,0,0-25.37,13.76A48.18,48.18,0,0,0,147,44.12a43.92,43.92,0,0,0-6.07,15.75c-1.8,9.59-1,20.1,1.59,30,1.69,6.38,10.17,23.94,13.21,29.35,11.34,20.22,4.5,33.12-4.62,39.23-5.49,3.68-29.39,6.88-37.41-23.83-1.28,2.22-2.45,4.52-3.52,6.86A84.55,84.55,0,0,0,103,167v0a64.12,64.12,0,0,0,128.25,0A60.68,60.68,0,0,0,229,151Z"/>
                </g>
              </svg>
            </div>
          </div>
        );

      case 'flame-pulse':
        return (
          <div className="loading-flame-pulse">
            <svg viewBox="0 0 230 270" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="grad1-pulse" x1="227.87" y1="108.66" x2="145.91" y2="260.44" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#ffb810"/>
                  <stop offset="0.43" stopColor="#fa406d"/>
                  <stop offset="1" stopColor="#ed2127"/>
                </linearGradient>
                <linearGradient id="grad2-pulse" x1="120.81" y1="25.04" x2="188.76" y2="234.11" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#ed2127"/>
                  <stop offset="0.57" stopColor="#fa406d"/>
                  <stop offset="1" stopColor="#ffb810"/>
                </linearGradient>
              </defs>
              <g transform="translate(-87, -20)">
                <path fill="url(#grad1-pulse)" d="M226.4,101.17a85.3,85.3,0,0,1,2.3,48.86,61.26,61.26,0,0,1,2.52,17A64.12,64.12,0,0,1,103,167v0a85.94,85.94,0,0,0-.6,10,85.19,85.19,0,1,0,124-75.81Z"/>
                <path fill="url(#grad2-pulse)" d="M229,151c-.09-.32-.17-.64-.26-1-5.83-20.31-26.67-50.54-38.05-69.19-4.92-8.06-9.18-16.1-11.8-24.22a53,53,0,0,1-2.75-15.81,44,44,0,0,1,4.8-20.34,67.78,67.78,0,0,0-25.37,13.76A48.18,48.18,0,0,0,147,44.12a43.92,43.92,0,0,0-6.07,15.75c-1.8,9.59-1,20.1,1.59,30,1.69,6.38,10.17,23.94,13.21,29.35,11.34,20.22,4.5,33.12-4.62,39.23-5.49,3.68-29.39,6.88-37.41-23.83-1.28,2.22-2.45,4.52-3.52,6.86A84.55,84.55,0,0,0,103,167v0a64.12,64.12,0,0,0,128.25,0A60.68,60.68,0,0,0,229,151Z"/>
              </g>
            </svg>
          </div>
        );

      case 'dual-ring':
        return (
          <div className="loading-dual-ring">
            <div className="ring ring-1"></div>
            <div className="ring ring-2"></div>
            <div className="flame-center">
              <svg viewBox="0 0 230 270" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="grad1-dual" x1="227.87" y1="108.66" x2="145.91" y2="260.44" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ffb810"/>
                    <stop offset="0.43" stopColor="#fa406d"/>
                    <stop offset="1" stopColor="#ed2127"/>
                  </linearGradient>
                  <linearGradient id="grad2-dual" x1="120.81" y1="25.04" x2="188.76" y2="234.11" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ed2127"/>
                    <stop offset="0.57" stopColor="#fa406d"/>
                    <stop offset="1" stopColor="#ffb810"/>
                  </linearGradient>
                </defs>
                <g transform="translate(-87, 0)">
                  <path fill="url(#grad1-dual)" d="M226.4,101.17a85.3,85.3,0,0,1,2.3,48.86,61.26,61.26,0,0,1,2.52,17A64.12,64.12,0,0,1,103,167v0a85.94,85.94,0,0,0-.6,10,85.19,85.19,0,1,0,124-75.81Z"/>
                  <path fill="url(#grad2-dual)" d="M229,151c-.09-.32-.17-.64-.26-1-5.83-20.31-26.67-50.54-38.05-69.19-4.92-8.06-9.18-16.1-11.8-24.22a53,53,0,0,1-2.75-15.81,44,44,0,0,1,4.8-20.34,67.78,67.78,0,0,0-25.37,13.76A48.18,48.18,0,0,0,147,44.12a43.92,43.92,0,0,0-6.07,15.75c-1.8,9.59-1,20.1,1.59,30,1.69,6.38,10.17,23.94,13.21,29.35,11.34,20.22,4.5,33.12-4.62,39.23-5.49,3.68-29.39,6.88-37.41-23.83-1.28,2.22-2.45,4.52-3.52,6.86A84.55,84.55,0,0,0,103,167v0a64.12,64.12,0,0,0,128.25,0A60.68,60.68,0,0,0,229,151Z"/>
                </g>
              </svg>
            </div>
          </div>
        );

      case 'perspective-spin':
        return (
          <div className="loading-perspective-spin">
            <div className="spin-ring spin-ring-1"></div>
            <div className="spin-ring spin-ring-2"></div>
            <div className="flame-center">
              <svg viewBox="0 0 230 270" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="grad1-persp" x1="227.87" y1="108.66" x2="145.91" y2="260.44" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ffb810"/>
                    <stop offset="0.43" stopColor="#fa406d"/>
                    <stop offset="1" stopColor="#ed2127"/>
                  </linearGradient>
                  <linearGradient id="grad2-persp" x1="120.81" y1="25.04" x2="188.76" y2="234.11" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#ed2127"/>
                    <stop offset="0.57" stopColor="#fa406d"/>
                    <stop offset="1" stopColor="#ffb810"/>
                  </linearGradient>
                </defs>
                <g transform="translate(-87, 0)">
                  <path fill="url(#grad1-persp)" d="M226.4,101.17a85.3,85.3,0,0,1,2.3,48.86,61.26,61.26,0,0,1,2.52,17A64.12,64.12,0,0,1,103,167v0a85.94,85.94,0,0,0-.6,10,85.19,85.19,0,1,0,124-75.81Z"/>
                  <path fill="url(#grad2-persp)" d="M229,151c-.09-.32-.17-.64-.26-1-5.83-20.31-26.67-50.54-38.05-69.19-4.92-8.06-9.18-16.1-11.8-24.22a53,53,0,0,1-2.75-15.81,44,44,0,0,1,4.8-20.34,67.78,67.78,0,0,0-25.37,13.76A48.18,48.18,0,0,0,147,44.12a43.92,43.92,0,0,0-6.07,15.75c-1.8,9.59-1,20.1,1.59,30,1.69,6.38,10.17,23.94,13.21,29.35,11.34,20.22,4.5,33.12-4.62,39.23-5.49,3.68-29.39,6.88-37.41-23.83-1.28,2.22-2.45,4.52-3.52,6.86A84.55,84.55,0,0,0,103,167v0a64.12,64.12,0,0,0,128.25,0A60.68,60.68,0,0,0,229,151Z"/>
                </g>
              </svg>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className={`loading-animation loading-animation--${variant} loading-animation--${size} ${className}`}
      role="status"
      aria-label="読み込み中"
    >
      {renderAnimation()}
      <span className="sr-only">読み込み中...</span>
    </div>
  );
};

export default LoadingAnimation;
